
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  Plus,
  Search,
  RefreshCw,
  RotateCcw,
  Image as ImageIcon,
  Store,
  Layers,
  Settings,
  ChevronLeft,
  ChevronRight,
  Images,
  PanelsTopLeft,
} from "lucide-react";
import {
  BannerCard,
  EmptyState,
  RestaurantCard,
  RestaurantSkeleton,
} from "./ChaafoCommonComponents";
import useChaafo from "../Services/Chaafo/useChaafo";
import ChaafoModals from "../Chaafo/ChaafoModel";
import CatalogHighlightsManager from "../Chaafo/CatalogHighlightsManager";
import { GlobalContext } from "../../Context/GlobalContext";
import useCatalogue from "../Services/Catalogue/useCatalogue";
import MessageConfigurationManager from "../Chaafo/MessageConfigurationManager";
import Banner from "../Domain/components/Banner/Banner";
import AppBanner from "../Domain/AppBanner";

const emptyCategoryForm = {
  categoryId: "",
  categoryName: "",
  categoryDescription: "",
  categoryImage: "",
  activeStatus: "Y",
};

const emptyBannerForm = {
  id: 0,
  fkUserId: "",
  selectedClientCode: "",
  offer: "",
  desc: "Home Screen",
  aspectRatio: "16:9",
  plateform: "App",
  imageUrl: "",
  imagePreviewUrl: "",
};

const normalizeSearchText = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const emptyRestaurantForm = {
  restaurantId: "",
  categoryId: "",
  userId: "",
  selectedClientCode: "",
  restaurantName: "",
  restaurantDescription: "",
  restaurantProfileImage: "",
  restaurantAddress: "",
  restaurantOffer: "",
  servingType: "V",
  ratings: "",
  startingPrice: "",
  activeStatus: "Y",
};

const getCategoryId = (item) =>
  item?.pk_category_id ||
  item?.category_id ||
  item?.fk_category_id ||
  item?.pk_restaurant_category_id ||
  item?.id ||
  "";

const getCategoryName = (item) =>
  item?.category_name ||
  item?.categoryName ||
  item?.restaurant_category_name ||
  item?.name ||
  "Untitled Category";

const getCategoryImage = (item) =>
  item?.category_image ||
  item?.categoryImage ||
  item?.restaurant_category_image ||
  item?.image ||
  "";

const getCategoryDescription = (item) =>
  item?.category_description ||
  item?.categoryDescription ||
  item?.restaurant_category_description ||
  item?.description ||
  "";

const getCategoryActiveStatus = (item) => {
  const value = String(item?.active_status ?? item?.activeStatus ?? "Y")
    .trim()
    .toUpperCase();

  return value === "N" ? "N" : "Y";
};

const getCategorySortOrder = (item) => {
  const rawValue =
    item?.item_sort_order ??
    item?.itemSortOrder ??
    item?.sort_order ??
    item?.sortOrder;

  if (
    rawValue === null ||
    rawValue === undefined ||
    String(rawValue).trim() === ""
  ) {
    return Number.MAX_SAFE_INTEGER;
  }

  const value = Number(rawValue);

  return Number.isFinite(value) ? value : Number.MAX_SAFE_INTEGER;
};

const isFixedAllCategory = (item) =>
  String(getCategoryName(item) || "")
    .trim()
    .toLowerCase() === "all";

const getRestaurantId = (item) =>
  item?.pk_restaurant_id || item?.restaurant_id || item?.id || "";

const getRestaurantName = (item) =>
  item?.restaurant_name || item?.restaurantName || "Restaurant";

const cleanRestaurantImage = (value = "") => {
  return String(value || "")
    .trim()
    .replace(/^"+|"+$/g, "")
    .replace(/\\"/g, "")
    .trim();
};

const RESTAURANT_IMAGE_BASE_URL = "https://bumppy.in/anlook/uploads/";

const getCompleteRestaurantImageUrl = (image = "") => {
  const cleanedImage = cleanRestaurantImage(image);

  if (!cleanedImage) {
    return "";
  }

  if (
    cleanedImage.startsWith("http://") ||
    cleanedImage.startsWith("https://") ||
    cleanedImage.startsWith("data:") ||
    cleanedImage.startsWith("blob:")
  ) {
    return cleanedImage;
  }

  return `${RESTAURANT_IMAGE_BASE_URL}${cleanedImage}`;
};

const getRestaurantAddress = (item) =>
  item?.restaurant_address || item?.restaurantAddress || "";

const getRestaurantOffer = (item) =>
  item?.restaurant_offer || item?.restaurantOffer || "";

const getStartingPrice = (item) =>
  item?.starting_price || item?.startingPrice || "";

const getServingType = (item) => item?.serving_type || item?.servingType || "";

const getRestaurantRatings = (item) => item?.ratings || item?.rating || "";

const getRestaurantActiveStatus = (item) => {
  const value = String(
    item?.active_status ?? item?.activeStatus ?? item?.status ?? "Y",
  )
    .trim()
    .toUpperCase();

  return value === "N" ? "N" : "Y";
};

const getRestaurantDescription = (item) =>
  item?.restaurant_description || item?.restaurantDescription || "";

// const getCreatedOn = (item) => item?.created_on || item?.createdOn || "";

const getBannerId = (item) =>
  item?.pk_banner_id || item?.id || item?.bannerId || item?.pk_id || 0;

const getBannerOffer = (item) =>
  item?.banner_type || item?.bannerType || item?.offer || "";

const getBannerImage = (item) =>
  getCompleteRestaurantImageUrl(
    item?.banner_image_url ||
      item?.bannerImageUrl ||
      item?.imageUrl ||
      item?.image_url ||
      item?.organization_imageUrl ||
      "",
  );

const getBannerUserId = (item) =>
  String(
    item?.shop_code ||
      item?.shopCode ||
      item?.fkUserId ||
      item?.fk_user_id ||
      item?.us_code ||
      item?.userCode ||
      item?.user_code ||
      item?.userId ||
      item?.user_id ||
      item?.userid ||
      "",
  ).trim();

const cleanFileNameText = (value = "") => {
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/%/g, "percent")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
};

export default function Cuisine() {
  const {
    loading,
    chaafoCategories,
    restaurantsByCategory,
    restaurantCategories,
    fetchRestaurantCategories,
    bannerDetails,
    bannerLoading,
    bannerError,
    fetchBannerDetails,
    insertBannerDetails,
    updateBannerDetails,
    deleteBannerDetails,
    fetchChaafoCategories,
    insertChaafoCategory,
    updateChaafoCategory,
    updateChaafoCategorySortOrder,
    deleteChaafoCategory,
    fetchRestaurantViaCategory,
    insertRestaurantViaCategory,
    updateRestaurantViaCategory,
    deleteRestaurantViaCategory,
    uploadChaafoImage,
    catalogFeatureLoading,
    catalogMenus,
    recommendedItems,
    offerItems,
    suggestionItems,
    fetchCatalogRecommendedAndOffers,
    fetchCatalogSuggestions,
    updateCatalogRecommended,
    updateCatalogOffers,
    updateCatalogSuggestion,
    messageConfigLoading,
    loginGatewayLoading,
    getMessageConfig,
    updateMessageConfig,
    loginGateway,
  } = useChaafo();

  const { getCatalogueItemsMaster } = useCatalogue();

  const [masterCatalogMenus, setMasterCatalogMenus] = useState([]);
  const [masterCatalogLoading, setMasterCatalogLoading] = useState(false);

  const [activeWorkspaceTab, setActiveWorkspaceTab] = useState("management");
  const [selectedHighlightClient, setSelectedHighlightClient] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [categorySearch, setCategorySearch] = useState("");
  const [restaurantSearch, setRestaurantSearch] = useState("");
  const [selectedServingType, setSelectedServingType] = useState("B");

  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showRestaurantModal, setShowRestaurantModal] = useState(false);

  const [categoryMode, setCategoryMode] = useState("add");
  const [restaurantMode, setRestaurantMode] = useState("add");

  const [categoryForm, setCategoryForm] = useState(emptyCategoryForm);
  const [restaurantForm, setRestaurantForm] = useState(emptyRestaurantForm);
  const [categoryRestaurantSearch, setCategoryRestaurantSearch] = useState("");
  const [categoryImageUploading, setCategoryImageUploading] = useState(false);
  const [categorySortApplied, setCategorySortApplied] = useState({});
  const [categorySortUpdatingId, setCategorySortUpdatingId] = useState("");
  const [draggedCategoryId, setDraggedCategoryId] = useState("");
  const [dragOverCategoryId, setDragOverCategoryId] = useState("");
  const [restaurantImageUploading, setRestaurantImageUploading] =
    useState(false);

  const [showBannerModal, setShowBannerModal] = useState(false);
  const [bannerForm, setBannerForm] = useState(emptyBannerForm);
  const [bannerImageUploading, setBannerImageUploading] = useState(false);
  const bannerScrollRef = useRef(null);
  const categorySliderRef = useRef(null);
  const restaurantSubmitRef = useRef(false);
  const [bannerPlatformFilter, setBannerPlatformFilter] = useState("");
  const [bannerTypeFilter, setBannerTypeFilter] = useState("");
  const [bannerShopCodeFilter, setBannerShopCodeFilter] = useState("");
  const [bannerOnTapFilter, setBannerOnTapFilter] = useState("");
  const [categoryType, setCategoryType] = useState("");

  const [configurationRefreshKey, setConfigurationRefreshKey] = useState(0);

  const [deleteModal, setDeleteModal] = useState({
    open: false,
    type: "",
    item: null,
    title: "",
    message: "",
  });

  const {
    adminId,
    AllClients = [],
  } = useContext(GlobalContext);

  const getRestaurantUserId = (item) =>
    String(
      item?.user_id ||
        item?.userid ||
        item?.userId ||
        item?.us_code ||
        item?.fk_user_id ||
        "",
    ).trim();

  const loadBannerDetails = async () => {
    return fetchBannerDetails({
      customerId: "",
      platform: "",
      ratio: "",
      shopCode: "",
      type: "",
    });
  };

  const loadCategoriesByType = async (typeValue = categoryType) => {
    return fetchChaafoCategories({
      type: typeValue,
      limit: 100,
      offset: 0,
    });
  };

  const handleCategoryTypeChange = async (value) => {
    setCategoryType(value);
    setCategorySearch("");
    setRestaurantSearch("");
    setCategoryRestaurantSearch("");

    const result = await loadCategoriesByType(value);

    const categories = Array.isArray(result?.response)
      ? result.response
      : [];

    const selectedStillExists = categories.some(
      (item) =>
        String(getCategoryId(item)) === String(selectedCategoryId),
    );

    if (!selectedStillExists) {
      const firstCategoryId = getCategoryId(categories?.[0]);

      setSelectedCategoryId(
        firstCategoryId ? String(firstCategoryId) : "",
      );
    }

    categorySliderRef.current?.scrollTo({
      left: 0,
      behavior: "smooth",
    });
  };

  const clients = useMemo(() => {
    return (Array.isArray(AllClients) ? AllClients : []).map((client) => ({
      ...client,
      us_code:
        client?.us_code ??
        client?.userid ??
        client?.userId ??
        client?.user_id ??
        client?.value ??
        client?.id ??
        "",

      us_name:
        client?.organization_name ??
        client?.us_name ??
        client?.us_loginid ??
        client?.us_email ??
        client?.name ??
        client?.label ??
        "Client",
    }));
  }, [AllClients]);

  const selectedHighlightClientUserId = useMemo(() => {
    return String(
      selectedHighlightClient?.userid ??
        selectedHighlightClient?.userId ??
        selectedHighlightClient?.user_id ??
        selectedHighlightClient?.us_code ??
        selectedHighlightClient?.value ??
        selectedHighlightClient?.id ??
        "",
    ).trim();
  }, [selectedHighlightClient]);

  const selectedHighlightClientCode = useMemo(() => {
    return String(
      selectedHighlightClient?.us_code ??
        selectedHighlightClient?.userid ??
        selectedHighlightClient?.userId ??
        selectedHighlightClient?.user_id ??
        selectedHighlightClient?.value ??
        selectedHighlightClient?.id ??
        "",
    ).trim();
  }, [selectedHighlightClient]);

  const handleHighlightClientChange = (client) => {
    setSelectedHighlightClient(client || null);
  };

  const extractApiList = (result) => {
    if (Array.isArray(result)) return result;

    if (Array.isArray(result?.response)) return result.response;
    if (Array.isArray(result?.data)) return result.data;
    if (Array.isArray(result?.details)) return result.details;

    if (Array.isArray(result?.response?.data)) return result.response.data;
    if (Array.isArray(result?.response?.response))
      return result.response.response;

    return [];
  };

  const fetchMasterCatalogMenus = async (
    clientUserId = selectedHighlightClientUserId,
  ) => {
    const finalUserId = String(clientUserId || "").trim();

    if (!finalUserId) {
      setMasterCatalogMenus([]);
      return null;
    }

    setMasterCatalogLoading(true);

    try {
      const result = await getCatalogueItemsMaster({
        userId: finalUserId,
      });

      const list = extractApiList(result);

      setMasterCatalogMenus(list);

      return result;
    } catch (error) {
      console.error("GET CATALOGUE ITEMS MASTER ERROR:", error);
      setMasterCatalogMenus([]);
      toast.error("Failed to load catalogue items");
      return null;
    } finally {
      setMasterCatalogLoading(false);
    }
  };

  const handleCategorySliderScroll = (direction) => {
    const container = categorySliderRef.current;

    if (!container) return;

    const firstCard = container.firstElementChild;

    if (!firstCard) return;

    const containerStyle = window.getComputedStyle(container);

    const gap =
      parseFloat(containerStyle.columnGap || containerStyle.gap || "0") || 0;

    const cardWidth = firstCard.getBoundingClientRect().width;

    const cardsPerPage = 10;
    const scrollAmount = (cardWidth + gap) * cardsPerPage;

    container.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

 const handlePageRefresh = async () => {
   if (activeWorkspaceTab === "configuration") {
     setConfigurationRefreshKey((prev) => prev + 1);
     return;
   }

   if (activeWorkspaceTab === "highlights") {
     if (!selectedHighlightClientUserId) {
       toast.error("Please select client");
       return;
     }

     await fetchMasterCatalogMenus(selectedHighlightClientUserId);

     return;
   }

   await Promise.all([
     loadCategoriesByType(),
     loadBannerDetails(),
     fetchRestaurantViaCategory({
       categoryIds: selectedCategoryId ? [selectedCategoryId] : [""],
       servingType: selectedServingType || "B",
     }),
   ]);
 };

  const handleInitialLoad = async () => {
    await Promise.all([
      loadCategoriesByType(""),
      loadBannerDetails(),
    ]);

    setSelectedCategoryId("");
  };

  const displayedRestaurantCategories = useMemo(() => {
    const categories = Array.isArray(chaafoCategories)
      ? [...chaafoCategories]
      : [];

    return categories.sort((a, b) => {
      const firstIsAll = isFixedAllCategory(a);
      const secondIsAll = isFixedAllCategory(b);

      if (firstIsAll && !secondIsAll) return -1;
      if (!firstIsAll && secondIsAll) return 1;

      const firstId = String(getCategoryId(a));
      const secondId = String(getCategoryId(b));

      const firstOrder =
        categorySortApplied[firstId] !== undefined
          ? Number(categorySortApplied[firstId])
          : getCategorySortOrder(a);

      const secondOrder =
        categorySortApplied[secondId] !== undefined
          ? Number(categorySortApplied[secondId])
          : getCategorySortOrder(b);

      if (firstOrder !== secondOrder) {
        return firstOrder - secondOrder;
      }

      return getCategoryName(a).localeCompare(getCategoryName(b));
    });
  }, [chaafoCategories, categorySortApplied]);

  const handleUpdateAllCategorySortOrders = async () => {
    if (!displayedRestaurantCategories.length) {
      toast.error("No categories found to update");
      return;
    }

    const categoryIds = displayedRestaurantCategories
      .map((item) => Number(getCategoryId(item)))
      .filter((id) => Number.isFinite(id));

    if (categoryIds.length !== displayedRestaurantCategories.length) {
      toast.error("Invalid category ID found");
      return;
    }

    const sortOrders = displayedRestaurantCategories.map(
      (_, index) => index + 1,
    );

    setCategorySortUpdatingId("all");

    try {
      const result = await updateChaafoCategorySortOrder({
        categoryIds,
        sortOrders,
      });

      if (!result) return;

      const nextApplied = {};

      displayedRestaurantCategories.forEach((item, index) => {
        nextApplied[String(getCategoryId(item))] = index + 1;
      });

      setCategorySortApplied(nextApplied);

      await loadCategoriesByType();

      categorySliderRef.current?.scrollTo({
        left: 0,
        behavior: "smooth",
      });
    } finally {
      setCategorySortUpdatingId("");
    }
  };

  const handleCategoryDragStart = (event, item) => {
    if (isFixedAllCategory(item)) {
      event.preventDefault();
      return;
    }

    const categoryId = String(getCategoryId(item) || "");

    if (!categoryId) return;

    setDraggedCategoryId(categoryId);
    setDragOverCategoryId("");

    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", categoryId);

    window.requestAnimationFrame(() => {
      categorySliderRef.current?.scrollTo({
        left: 0,
        behavior: "smooth",
      });
    });
  };

  const handleCategoryDragOver = (event, item) => {
    event.preventDefault();

    const targetCategoryId = String(getCategoryId(item) || "");

    if (!targetCategoryId || targetCategoryId === draggedCategoryId) return;

    event.dataTransfer.dropEffect = "move";
    setDragOverCategoryId(targetCategoryId);
  };

  const handleCategorySliderDragOver = (event) => {
    if (!draggedCategoryId) return;

    event.preventDefault();

    const container = categorySliderRef.current;

    if (!container) return;

    const rect = container.getBoundingClientRect();
    const edgeSize = Math.min(140, rect.width * 0.16);
    const distanceFromLeft = event.clientX - rect.left;
    const distanceFromRight = rect.right - event.clientX;

    // Drag ko left/right edge ke paas hold karne par continuous auto-scroll.
    if (distanceFromLeft < edgeSize) {
      const strength = Math.max(1, (edgeSize - distanceFromLeft) / edgeSize);

      container.scrollBy({
        left: -Math.ceil(28 * strength),
        behavior: "auto",
      });
    } else if (distanceFromRight < edgeSize) {
      const strength = Math.max(1, (edgeSize - distanceFromRight) / edgeSize);

      container.scrollBy({
        left: Math.ceil(28 * strength),
        behavior: "auto",
      });
    }
  };

  const handleCategoryDrop = (event, targetItem) => {
    event.preventDefault();

    const sourceCategoryId = String(
      draggedCategoryId || event.dataTransfer.getData("text/plain") || "",
    );
    const targetCategoryId = String(getCategoryId(targetItem) || "");

    setDragOverCategoryId("");

    const sourceItem = displayedRestaurantCategories.find(
      (item) => String(getCategoryId(item)) === sourceCategoryId,
    );

    if (
      !sourceCategoryId ||
      !targetCategoryId ||
      sourceCategoryId === targetCategoryId ||
      isFixedAllCategory(sourceItem)
    ) {
      setDraggedCategoryId("");
      return;
    }

    const reorderedCategories = [...displayedRestaurantCategories];
    const sourceIndex = reorderedCategories.findIndex(
      (item) => String(getCategoryId(item)) === sourceCategoryId,
    );
    const targetIndex = reorderedCategories.findIndex(
      (item) => String(getCategoryId(item)) === targetCategoryId,
    );

    if (sourceIndex === -1 || targetIndex === -1) {
      setDraggedCategoryId("");
      return;
    }

    const [movedCategory] = reorderedCategories.splice(sourceIndex, 1);

    // All is always index 0. Dropping on All means move category to index 1
    // i.e. visible position 2.
    const safeTargetIndex = isFixedAllCategory(targetItem)
      ? 1
      : Math.max(1, targetIndex);

    reorderedCategories.splice(safeTargetIndex, 0, movedCategory);

    // Safety: force All back to first even if browser drag/drop gives an edge case.
    const allIndex = reorderedCategories.findIndex(isFixedAllCategory);

    if (allIndex > 0) {
      const [allCategory] = reorderedCategories.splice(allIndex, 1);
      reorderedCategories.unshift(allCategory);
    }

    const nextApplied = {};
    reorderedCategories.forEach((item, index) => {
      nextApplied[String(getCategoryId(item))] = index + 1;
    });

    setCategorySortApplied(nextApplied);
    setDraggedCategoryId("");
  };

  const handleCategoryDragEnd = () => {
    setDraggedCategoryId("");
    setDragOverCategoryId("");
  };

  const getCategoryRestaurantCount = (item) => {
    const apiCount =
      item?.restaurant_count ||
      item?.restaurantCount ||
      item?.total_restaurants ||
      item?.totalRestaurants ||
      item?.restaurant_no ||
      item?.restaurantNo ||
      item?.count;

    if (apiCount !== undefined && apiCount !== null && apiCount !== "") {
      return apiCount;
    }

    return 0;
  };

  const selectedCategory = useMemo(() => {
    return displayedRestaurantCategories?.find(
      (item) => String(getCategoryId(item)) === String(selectedCategoryId),
    );
  }, [displayedRestaurantCategories, selectedCategoryId]);

  const filteredCategories = useMemo(() => {
    const search = normalizeSearchText(categorySearch);

    const categories = Array.isArray(displayedRestaurantCategories)
      ? displayedRestaurantCategories
      : [];

    if (!search) return categories;

    return categories.filter((item) => {
      const searchableText = normalizeSearchText(
        [
          getCategoryName(item),
          getCategoryDescription(item),
          getCategoryId(item),
          getCategoryActiveStatus(item) === "Y" ? "active" : "inactive",
        ].join(" "),
      );

      return searchableText.includes(search);
    });
  }, [displayedRestaurantCategories, categorySearch]);

  const bannerFilterOptions = useMemo(() => {
    const banners = Array.isArray(bannerDetails) ? bannerDetails : [];

    const getUniqueValues = (key) =>
      [
        ...new Set(
          banners
            .map((item) => String(item?.[key] || "").trim())
            .filter(Boolean),
        ),
      ].sort((a, b) => a.localeCompare(b));

    return {
      platforms: getUniqueValues("plateform"),
      bannerTypes: getUniqueValues("banner_type"),
      shopCodes: getUniqueValues("shop_code"),
      onTapValues: getUniqueValues("on_tap"),
    };
  }, [bannerDetails]);

  const filteredBannerDetails = useMemo(() => {
    const banners = Array.isArray(bannerDetails) ? bannerDetails : [];

    return banners.filter((item) => {
      const platform = String(item?.plateform || "").trim();
      const bannerType = String(item?.banner_type || "").trim();
      const shopCode = String(item?.shop_code || "").trim();
      const onTap = String(item?.on_tap || "").trim();

      const platformMatch =
        !bannerPlatformFilter || platform === bannerPlatformFilter;

      const bannerTypeMatch =
        !bannerTypeFilter || bannerType === bannerTypeFilter;

      const shopCodeMatch =
        !bannerShopCodeFilter || shopCode === bannerShopCodeFilter;

      const onTapMatch = !bannerOnTapFilter || onTap === bannerOnTapFilter;

      return platformMatch && bannerTypeMatch && shopCodeMatch && onTapMatch;
    });
  }, [
    bannerDetails,
    bannerPlatformFilter,
    bannerTypeFilter,
    bannerShopCodeFilter,
    bannerOnTapFilter,
  ]);

  const hasBannerFilters =
    Boolean(bannerPlatformFilter) ||
    Boolean(bannerTypeFilter) ||
    Boolean(bannerShopCodeFilter) ||
    Boolean(bannerOnTapFilter);

  const handleResetBannerFilters = () => {
    setBannerPlatformFilter("");
    setBannerTypeFilter("");
    setBannerShopCodeFilter("");
    setBannerOnTapFilter("");
  };

  const isGlobalRestaurantSearchActive = Boolean(
    normalizeSearchText(restaurantSearch),
  );

  const filteredRestaurants = useMemo(() => {
    const search = normalizeSearchText(
      isGlobalRestaurantSearchActive
        ? restaurantSearch
        : categoryRestaurantSearch,
    );

    const restaurants = Array.isArray(restaurantsByCategory)
      ? restaurantsByCategory
      : [];

    if (!search) return restaurants;

    return restaurants.filter((item) => {
      const servingType = getServingType(item);

      const servingTypeText =
        servingType === "V"
          ? "veg vegetarian"
          : servingType === "N"
            ? "non veg nonvegetarian"
            : servingType === "B"
              ? "both veg non veg"
              : servingType;

      const searchableText = normalizeSearchText(
        [
          getRestaurantName(item),
          getRestaurantDescription(item),
          getRestaurantAddress(item),
          getRestaurantOffer(item),
          getStartingPrice(item),
          getRestaurantRatings(item),
          servingTypeText,
          getRestaurantId(item),
        ].join(" "),
      );

      return searchableText.includes(search);
    });
  }, [
    restaurantsByCategory,
    restaurantSearch,
    categoryRestaurantSearch,
    isGlobalRestaurantSearchActive,
  ]);

  const handleSaveRestaurant = async (e) => {
    e.preventDefault();

    if (restaurantSubmitRef.current) return;

    if (!restaurantForm.userId) {
      toast.error("Please select restaurant user ID");
      return;
    }

    if (!restaurantForm.categoryId) {
      toast.error("Please select category");
      return;
    }

    if (!restaurantForm.servingType) {
      toast.error("Please select serving type");
      return;
    }

    const payload = {
      ...restaurantForm,
      userId: restaurantForm.userId,
    };

    restaurantSubmitRef.current = true;

    try {
      let result = null;

      // IMPORTANT: mutation is always called first.
      if (restaurantMode === "edit") {
        result = await updateRestaurantViaCategory(payload);
      } else {
        result = await insertRestaurantViaCategory(payload);
      }

      if (!result) return;

      setShowRestaurantModal(false);
      setRestaurantForm(emptyRestaurantForm);

      // Refresh only AFTER insert/update has completed successfully.
      await fetchRestaurantViaCategory({
        categoryIds: selectedCategoryId ? [selectedCategoryId] : [""],
        servingType: selectedServingType || "B",
      });
    } finally {
      restaurantSubmitRef.current = false;
    }
  };

  const openAddBannerModal = () => {
    setBannerForm({
      ...emptyBannerForm,
      fkUserId: "",
      selectedClientCode: "",
    });

    setShowBannerModal(true);
  };

  const normalizeBannerPlatform = (value = "") => {
    const text = String(value || "")
      .trim()
      .toLowerCase();

    if (text === "web") return "Web";
    if (text === "both") return "Both";
    if (text === "app" || text === "android") return "App";

    return "App";
  };

  const normalizeBannerOnTap = (value = "") => {
    const text = String(value || "")
      .trim()
      .toLowerCase();

    if (text === "main menu" || text === "main_menu" || text === "menu") {
      return "Main Menu";
    }

    return "Home Screen";
  };

  const openEditBannerModal = (item) => {
    const imageUrl = getBannerImage(item);
    const bannerUserId = getBannerUserId(item);

    setBannerForm({
      id: getBannerId(item),
      fkUserId: bannerUserId,
      selectedClientCode: bannerUserId,

      offer: item?.banner_type || item?.bannerType || item?.offer || "",
      desc: normalizeBannerOnTap(
        item?.on_tap || item?.onTap || item?.desc || item?.description || "",
      ),
      aspectRatio: item?.aspect_ratio || item?.aspectRatio || "16:9",
      plateform: normalizeBannerPlatform(item?.plateform || item?.platform),

      imageUrl,
      imagePreviewUrl: imageUrl,
    });

    setShowBannerModal(true);
  };

  const getUploadedImageUrl = (result) => {
    const responseData = Array.isArray(result?.response)
      ? result.response[0]
      : result?.response;

    const data = Array.isArray(result?.data) ? result.data[0] : result?.data;

    const possibleUrl =
      result?.image_url ||
      result?.imageUrl ||
      result?.url ||
      result?.file_url ||
      result?.fileUrl ||
      result?.file_name ||
      result?.fileName ||
      result?.filename ||
      result?.image_name ||
      result?.imageName ||
      result?.name ||
      result?.path ||
      data?.image_url ||
      data?.imageUrl ||
      data?.url ||
      data?.file_url ||
      data?.fileUrl ||
      data?.file_name ||
      data?.fileName ||
      data?.filename ||
      data?.image_name ||
      data?.imageName ||
      data?.name ||
      data?.path ||
      responseData?.image_url ||
      responseData?.imageUrl ||
      responseData?.url ||
      responseData?.file_url ||
      responseData?.fileUrl ||
      responseData?.file_name ||
      responseData?.fileName ||
      responseData?.filename ||
      responseData?.image_name ||
      responseData?.imageName ||
      responseData?.name ||
      responseData?.path ||
      (typeof responseData === "string" ? responseData : "");

    return String(possibleUrl || "")
      .trim()
      .replace(/\s+/g, "");
  };

  const handleBannerImageUpload = async (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file");
      return;
    }

    const localPreviewUrl = URL.createObjectURL(file);

    setBannerForm((prev) => ({
      ...prev,
      imagePreviewUrl: localPreviewUrl,
    }));

    setBannerImageUploading(true);

    try {
      const safeBannerImageName =
        cleanFileNameText(bannerForm.offer) ||
        cleanFileNameText(file.name) ||
        "banner_image";

      const result = await uploadChaafoImage({
        file,
        itemName: safeBannerImageName,
      });

      console.log("BANNER IMAGE UPLOAD RESPONSE:", result);

      const uploadedUrl = getUploadedImageUrl(result);

      console.log("FINAL BANNER IMAGE URL:", uploadedUrl);

      if (uploadedUrl) {
        setBannerForm((prev) => ({
          ...prev,
          imageUrl: uploadedUrl,
          imagePreviewUrl: uploadedUrl.startsWith("http")
            ? uploadedUrl
            : localPreviewUrl,
        }));

        toast.success("Banner image uploaded");
      } else {
        console.error("UPLOAD RESPONSE URL NOT FOUND:", result);
        toast.error("Image uploaded but URL not found in response");
      }
    } catch (error) {
      console.error("Banner image upload error:", error);
      toast.error("Banner image upload failed");
    } finally {
      setBannerImageUploading(false);
      e.target.value = "";
    }
  };

  const handleSaveBanner = async (e) => {
    e.preventDefault();

    if (!bannerForm.fkUserId) {
      toast.error("Please select client ID");
      return;
    }

    if (!bannerForm.offer) {
      toast.error("Please enter banner type");
      return;
    }

    if (!bannerForm.imageUrl) {
      toast.error("Please upload banner image first");
      return;
    }

    const payload = {
      fkUserId: bannerForm.fkUserId,
      offer: bannerForm.offer,
      desc: bannerForm.desc || "Home Screen",
      aspectRatio: bannerForm.aspectRatio || "16:9",
      plateform: bannerForm.plateform || "App",
      imageUrl: String(bannerForm.imageUrl || "")
        .trim()
        .replace(/\s+/g, ""),
    };

    const result = bannerForm.id
      ? await updateBannerDetails({
          id: bannerForm.id,
          ...payload,
        })
      : await insertBannerDetails(payload);

    if (result) {
      setShowBannerModal(false);
      setBannerForm(emptyBannerForm);
      await loadBannerDetails();
    }
  };

  const handleDeleteBanner = (item) => {
    const bannerUserId = getBannerUserId(item);

    console.log("DELETE BANNER ITEM:", item);
    console.log("DELETE BANNER USER ID:", bannerUserId);

    setDeleteModal({
      open: true,
      type: "banner",
      item,
      title: "Delete Banner",
      message: `Are you sure you want to delete this banner "${getBannerOffer(
        item,
      )}"?`,
      fkUserId: bannerUserId,
      selectedClientCode: bannerUserId,
    });
  };

  const openAddCategoryModal = () => {
    setCategoryMode("add");
    setCategoryForm({
      ...emptyCategoryForm,
      activeStatus: "Y",
    });
    setShowCategoryModal(true);
  };

  const openEditCategoryModal = (item) => {
    setCategoryMode("edit");
    setCategoryForm({
      categoryId: getCategoryId(item),
      categoryName: getCategoryName(item),
      categoryDescription: getCategoryDescription(item),
      categoryImage: getCategoryImage(item),
      activeStatus: getCategoryActiveStatus(item),
    });
    setShowCategoryModal(true);
  };

  const openAddRestaurantModal = () => {
    if (!selectedCategoryId) {
      toast.error("Please select category");
      return;
    }

    setRestaurantMode("add");

    setRestaurantForm({
      ...emptyRestaurantForm,
      categoryId: selectedCategoryId,
      servingType: selectedServingType || "B",
      userId: "",
      selectedClientCode: "",
    });

    setShowRestaurantModal(true);
  };

  const openEditRestaurantModal = (item) => {
    const restaurantUserId = getRestaurantUserId(item);

    setRestaurantMode("edit");

    setRestaurantForm({
      restaurantId: getRestaurantId(item),
      categoryId:
        item?.fk_category_id || item?.category_id || selectedCategoryId,

      userId: restaurantUserId,
      selectedClientCode: restaurantUserId,

      restaurantName: getRestaurantName(item),
      restaurantDescription: getRestaurantDescription(item),
      restaurantProfileImage:
        item?.restaurant_profile_image || item?.restaurantProfileImage || "",
      restaurantAddress: getRestaurantAddress(item),
      restaurantOffer: getRestaurantOffer(item),
      servingType: getServingType(item) || selectedServingType || "V",
      ratings: getRestaurantRatings(item),
      startingPrice: getStartingPrice(item),
      activeStatus: getRestaurantActiveStatus(item),
    });

    setShowRestaurantModal(true);
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();

    if (!categoryForm.categoryName.trim()) {
      toast.error("Please enter category name");
      return;
    }

    const payload = {
      ...categoryForm,
    };

    let result = null;

    if (categoryMode === "edit") {
      result = await updateChaafoCategory(payload);
    } else {
      result = await insertChaafoCategory(payload);
    }

    if (result) {
      setShowCategoryModal(false);
      setCategoryForm(emptyCategoryForm);

      const refreshed = await loadCategoriesByType();

      const categories = Array.isArray(refreshed?.response)
        ? refreshed.response
        : [];

      const selectedStillExists = categories.some(
        (item) => String(getCategoryId(item)) === String(selectedCategoryId),
      );

      if (!selectedStillExists) {
        const firstCategoryId = getCategoryId(categories[0]);

        setSelectedCategoryId(firstCategoryId ? String(firstCategoryId) : "");
      }
    }
  };

  const handleDeleteCategory = (item) => {
    setDeleteModal({
      open: true,
      type: "category",
      item,
      title: "Delete Category",
      message: `Are you sure you want to delete "${getCategoryName(item)}"?`,
    });
  };

  const handleCategoryImageUpload = async (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file");
      return;
    }

    setCategoryImageUploading(true);

    try {
      const result = await uploadChaafoImage({
        file,
        itemName: categoryForm.categoryName || "category_image",
      });

      const uploadedUrl =
        result?.image_url ||
        result?.imageUrl ||
        result?.url ||
        result?.file_url ||
        result?.fileUrl ||
        result?.response?.image_url ||
        result?.response?.imageUrl ||
        result?.response?.url ||
        result?.response;

      if (uploadedUrl) {
        setCategoryForm((prev) => ({
          ...prev,
          categoryImage: String(uploadedUrl),
        }));
      } else {
        toast.error("Image uploaded but URL not found in response");
      }
    } finally {
      setCategoryImageUploading(false);
      e.target.value = "";
    }
  };

  const handleRestaurantImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);

    if (!files.length) return;

    const invalidFile = files.find((file) => !file.type.startsWith("image/"));

    if (invalidFile) {
      toast.error("Please select valid image files only");
      return;
    }

    setRestaurantImageUploading(true);

    try {
      const uploadedUrls = [];

      for (const file of files) {
        const result = await uploadChaafoImage({
          file,
          itemName:
            restaurantForm.restaurantName ||
            restaurantForm.restaurantDescription ||
            file.name ||
            "restaurant_image",
        });

        const uploadedUrl =
          result?.image_url ||
          result?.imageUrl ||
          result?.url ||
          result?.file_url ||
          result?.fileUrl ||
          result?.response?.image_url ||
          result?.response?.imageUrl ||
          result?.response?.url ||
          result?.response;

        if (uploadedUrl) {
          uploadedUrls.push(String(uploadedUrl));
        } else {
          toast.error(
            `Image "${file.name}" uploaded but URL not found in response`,
          );
        }
      }

      if (uploadedUrls.length) {
        setRestaurantForm((prev) => {
          const oldImages = prev.restaurantProfileImage
            ? prev.restaurantProfileImage
                .split(",")
                .map((img) => img.trim())
                .filter(Boolean)
            : [];

          return {
            ...prev,
            restaurantProfileImage: [...oldImages, ...uploadedUrls].join(", "),
          };
        });
      } else {
        toast.error("Image uploaded but URL not found in response");
      }
    } finally {
      setRestaurantImageUploading(false);
      e.target.value = "";
    }
  };

  const handleServingTypeChange = (value) => {
    setSelectedServingType(value);
    setRestaurantSearch("");
    setCategoryRestaurantSearch("");
  };

  const handleResetFilters = async () => {
    setCategorySearch("");
    setRestaurantSearch("");
    setCategoryRestaurantSearch("");
    setSelectedServingType("B");
    setCategoryType("");

    const refreshed = await loadCategoriesByType("");

    const categories = Array.isArray(refreshed?.response)
      ? refreshed.response
      : [];

    const firstCategoryId = getCategoryId(categories?.[0]);

    setSelectedCategoryId(
      firstCategoryId ? String(firstCategoryId) : "",
    );

    categorySliderRef.current?.scrollTo({
      left: 0,
      behavior: "smooth",
    });
  };

  const handleDeleteRestaurant = (item) => {
    setDeleteModal({
      open: true,
      type: "restaurant",
      item,
      title: "Delete Restaurant",
      message: `Are you sure you want to delete "${getRestaurantName(item)}"?`,
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteModal.item || !deleteModal.type) return;

    if (deleteModal.type === "category") {
      const categoryId = getCategoryId(deleteModal.item);

      const result = await deleteChaafoCategory(categoryId);

      if (result) {
        const refreshed = await loadCategoriesByType();

        const categories = Array.isArray(refreshed?.response)
          ? refreshed.response
          : [];

        if (String(selectedCategoryId) === String(categoryId)) {
          const nextCategoryId = getCategoryId(categories?.[0]);

          setSelectedCategoryId(nextCategoryId ? String(nextCategoryId) : "");
        }

        setDeleteModal({
          open: false,
          type: "",
          item: null,
          title: "",
          message: "",
          fkUserId: "",
          selectedClientCode: "",
        });
      }

      return;
    }

    if (deleteModal.type === "restaurant") {
      const restaurantId = getRestaurantId(deleteModal.item);

      const result = await deleteRestaurantViaCategory(restaurantId);

      if (result) {
        await fetchRestaurantViaCategory({
          categoryIds: [selectedCategoryId],
          servingType: selectedServingType || "B",
        });

        setDeleteModal({
          open: false,
          type: "",
          item: null,
          title: "",
          message: "",
          fkUserId: "",
          selectedClientCode: "",
        });
      }

      return;
    }

    if (deleteModal.type === "banner") {
      const bannerId = String(getBannerId(deleteModal.item) || "").trim();

      if (!bannerId) {
        toast.error("Banner ID not found");
        return;
      }

      const payload = {
        id: bannerId,
      };

      const result = await deleteBannerDetails(payload.id);

      if (result) {
        await loadBannerDetails();

        setDeleteModal({
          open: false,
          type: "",
          item: null,
          title: "",
          message: "",
          fkUserId: "",
          selectedClientCode: "",
        });
      }

      return;
    }
  };

  const loopedBannerDetails = useMemo(() => {
    const list = Array.isArray(filteredBannerDetails)
      ? filteredBannerDetails
      : [];

    if (list.length <= 4) {
      return list;
    }

    return [...list, ...list];
  }, [filteredBannerDetails]);

  useEffect(() => {
    const search = normalizeSearchText(categorySearch);

    if (!search) return;
    if (!filteredCategories.length) return;

    const selectedCategoryIsVisible = filteredCategories.some(
      (item) => String(getCategoryId(item)) === String(selectedCategoryId),
    );

    if (!selectedCategoryIsVisible) {
      const firstMatchedCategoryId = getCategoryId(filteredCategories[0]);

      if (firstMatchedCategoryId) {
        setSelectedCategoryId(String(firstMatchedCategoryId));
      }
    }
  }, [categorySearch, filteredCategories, selectedCategoryId]);

  useEffect(() => {
    handleInitialLoad();
  }, []);

  useEffect(() => {
    const container = bannerScrollRef.current;

    if (!container) return;

    // Filter change hone par start se show karo
    container.scrollTo({
      left: 0,
      behavior: "auto",
    });

    if (
      !Array.isArray(filteredBannerDetails) ||
      filteredBannerDetails.length <= 4
    ) {
      return;
    }

    let resetTimer = null;

    const getScrollStep = () => {
      const firstCard = container.firstElementChild;

      if (!firstCard) return 0;

      const containerStyle = window.getComputedStyle(container);

      const gap =
        parseFloat(containerStyle.columnGap || containerStyle.gap || "0") || 0;

      return firstCard.getBoundingClientRect().width + gap;
    };

    const interval = window.setInterval(() => {
      const currentContainer = bannerScrollRef.current;

      if (!currentContainer) return;

      const scrollStep = getScrollStep();

      if (!scrollStep) return;

      const originalListWidth = filteredBannerDetails.length * scrollStep;
      const nextLeft = currentContainer.scrollLeft + scrollStep;

      currentContainer.scrollTo({
        left: nextLeft,
        behavior: "smooth",
      });

      if (nextLeft >= originalListWidth) {
        if (resetTimer) {
          window.clearTimeout(resetTimer);
        }

        resetTimer = window.setTimeout(() => {
          const currentLeft = currentContainer.scrollLeft;

          currentContainer.scrollTo({
            left: currentLeft - originalListWidth,
            behavior: "auto",
          });
        }, 600);
      }
    }, 2500);

    return () => {
      window.clearInterval(interval);

      if (resetTimer) {
        window.clearTimeout(resetTimer);
      }
    };
  }, [filteredBannerDetails]);

  useEffect(() => {
    if (showRestaurantModal || restaurantSubmitRef.current) return;

    fetchRestaurantViaCategory({
      categoryIds: isGlobalRestaurantSearchActive
        ? [""]
        : selectedCategoryId
          ? [selectedCategoryId]
          : [""],

      servingType: selectedServingType || "B",
    });
  }, [selectedCategoryId, selectedServingType, isGlobalRestaurantSearchActive]);

  return (
    <div className="min-h-[calc(100vh-64px)] p-4 sm:p-6">
      <div className="mx-auto w-full max-w-[1800px]">
        {/* FIXED HEADER */}
        <div className="z-40 shrink-0 bg-slate-50 pb-4">
          {/* TOP TITLE ROW */}
          <div className="flex flex-col gap-4 px-1 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600">
                <Store size={20} />
              </div>

              <div>
                <h1 className="text-xl font-bold text-slate-900">
                  {activeWorkspaceTab === "management"
                    ? "Categories & Restaurants"
                    : activeWorkspaceTab === "highlights"
                      ? "Catalog Highlights"
                      : activeWorkspaceTab === "configuration"
                        ? "Message Configuration"
                        : activeWorkspaceTab === "banner"
                          ? "Banner Management"
                          : "App Banner Management"}
                </h1>

                <p className="mt-0.5 text-xs text-slate-500">
                  {activeWorkspaceTab === "management"
                    ? "Manage categories, restaurants and promotional banners"
                    : activeWorkspaceTab === "highlights"
                      ? "Manage recommended items, offers and suggestions"
                      : activeWorkspaceTab === "configuration"
                        ? "Manage OTP partners and test the login gateway"
                        : activeWorkspaceTab === "banner"
                          ? "Manage website and promotional banners"
                          : "Manage application banners"}
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={handlePageRefresh}
                disabled={
                  loading ||
                  catalogFeatureLoading ||
                  messageConfigLoading ||
                  loginGatewayLoading ||
                  (activeWorkspaceTab === "highlights" &&
                    !selectedHighlightClientUserId)
                }
                className="inline-flex w-fit items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <RefreshCw
                  size={16}
                  className={
                    loading || catalogFeatureLoading ? "animate-spin" : ""
                  }
                />
                Refresh
              </button>
            </div>
          </div>

          {/* TOP WORKSPACE TABS */}
          <div className="mt-4 flex w-fit max-w-full gap-1 overflow-x-auto rounded-2xl border border-slate-200 bg-white p-1.5 shadow-sm">
            {[
              {
                key: "management",
                label: "Categories & Restaurants",
                icon: <Store size={16} />,
              },
              {
                key: "highlights",
                label: "Recommended, Offers & Suggestions",
                icon: <Layers size={16} />,
              },
              {
                key: "configuration",
                label: "Configuration",
                icon: <Settings size={16} />,
              },
              {
                key: "appBanner",
                label: "App Banner",
                icon: <PanelsTopLeft size={16} />,
              },
              {
                key: "banner",
                label: "Banner",
                icon: <Images size={16} />,
              },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveWorkspaceTab(tab.key)}
                className={`inline-flex shrink-0 items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold transition ${
                  activeWorkspaceTab === tab.key
                    ? "bg-emerald-600 text-white shadow-sm"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>

          {/* ACTION / FILTER CARD */}
          {activeWorkspaceTab === "management" && (
            <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800">
                <Settings size={16} className="text-slate-500" />
                Management Actions
              </div>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-4 xl:grid-cols-7">
                {/* CATEGORY SEARCH */}
                <div className="min-w-0">
                  <label className="mb-1 block text-[11px] font-medium text-slate-600">
                    Category Search
                  </label>

                  <div className="flex h-10 w-full items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 transition focus-within:border-emerald-400 focus-within:ring-2 focus-within:ring-emerald-100">
                    <Search size={14} className="shrink-0 text-slate-400" />

                    <input
                      value={categorySearch}
                      onChange={(e) => setCategorySearch(e.target.value)}
                      placeholder="Search categories..."
                      className="min-w-0 flex-1 bg-transparent text-xs text-slate-700 outline-none placeholder:text-slate-400"
                    />
                  </div>
                </div>

                {/* CATEGORY TYPE */}
                <div className="min-w-0">
                  <label className="mb-1 block text-[11px] font-medium text-slate-600">
                    Category Type
                  </label>

                  <select
                    value={categoryType}
                    onChange={(e) => handleCategoryTypeChange(e.target.value)}
                    className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 outline-none transition focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                  >
                    <option value="">All</option>
                    <option value="0">Type 0</option>
                    <option value="1">Type 1</option>
                  </select>
                </div>

                {/* RESTAURANT SEARCH */}
                <div className="min-w-0">
                  <label className="mb-1 block text-[11px] font-medium text-slate-600">
                    Restaurant Search
                  </label>

                  <div className="flex h-10 w-full items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 transition focus-within:border-emerald-400 focus-within:ring-2 focus-within:ring-emerald-100">
                    <Search size={14} className="shrink-0 text-slate-400" />

                    <input
                      value={restaurantSearch}
                      onChange={(e) => {
                        const value = e.target.value;

                        setRestaurantSearch(value);

                        if (value.trim()) {
                          setCategoryRestaurantSearch("");
                        }
                      }}
                      placeholder="Search restaurant..."
                      className="min-w-0 flex-1 bg-transparent text-xs text-slate-700 outline-none placeholder:text-slate-400"
                    />
                  </div>
                </div>

                {/* SERVING TYPE */}
                <div className="min-w-0">
                  <label className="mb-1 block text-[11px] font-medium text-slate-600">
                    Serving Type
                  </label>

                  <select
                    value={selectedServingType}
                    onChange={(e) => handleServingTypeChange(e.target.value)}
                    className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 outline-none transition focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                  >
                    <option value="B">Both</option>
                    <option value="V">Veg</option>
                    <option value="N">Non-Veg</option>
                  </select>
                </div>

                {/* RESET */}
                <div className="min-w-0">
                  <label className="invisible mb-1 block text-[11px] font-medium">
                    Action
                  </label>

                  <button
                    type="button"
                    onClick={handleResetFilters}
                    className="inline-flex h-10 w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                  >
                    <RotateCcw size={14} />
                    Reset
                  </button>
                </div>

                {adminId === "3" || adminId === "12" || adminId === "13" ? (
                  <>
                    {/* ADD CATEGORY */}
                    <div className="min-w-0">
                      <label className="invisible mb-1 block text-[11px] font-medium">
                        Action
                      </label>

                      <button
                        type="button"
                        onClick={openAddCategoryModal}
                        className="inline-flex h-10 w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-blue-600 px-3 text-xs font-semibold text-white transition hover:bg-blue-700"
                      >
                        <Plus size={14} />
                        Add Category
                      </button>
                    </div>

                    {/* ADD BANNER */}
                    <div className="min-w-0">
                      <label className="invisible mb-1 block text-[11px] font-medium">
                        Action
                      </label>

                      <button
                        type="button"
                        onClick={openAddBannerModal}
                        className="inline-flex h-10 w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-emerald-600 px-3 text-xs font-semibold text-white transition hover:bg-emerald-700"
                      >
                        <Plus size={14} />
                        Add Banner
                      </button>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 space-y-6">
          {activeWorkspaceTab === "management" ? (
            <>
              {/* BANNER SECTION */}
              <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">
                      Banner Management
                    </h2>
                    <p className="text-sm text-slate-500">
                      {hasBannerFilters
                        ? `${filteredBannerDetails.length} of ${bannerDetails?.length || 0} banners`
                        : `${bannerDetails?.length || 0} banners configured`}
                    </p>
                  </div>

                  {adminId === "3" || adminId === "12" || adminId === "13" ? (
                    <button
                      onClick={openAddBannerModal}
                      className="inline-flex items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-green-700"
                    >
                      <Plus size={17} />
                      Add Banner
                    </button>
                  ) : (
                    ""
                  )}
                </div>

                <div className="mb-5 rounded-2xl border border-slate-200 bg-slate-50 p-3 sm:p-4">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
                    {/* PLATFORM */}
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                        Platform
                      </label>

                      <select
                        value={bannerPlatformFilter}
                        onChange={(e) =>
                          setBannerPlatformFilter(e.target.value)
                        }
                        className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                      >
                        <option value="">All Platforms</option>

                        {bannerFilterOptions.platforms.map((platform) => (
                          <option key={platform} value={platform}>
                            {platform}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* BANNER TYPE */}
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                        Banner Type
                      </label>

                      <select
                        value={bannerTypeFilter}
                        onChange={(e) => setBannerTypeFilter(e.target.value)}
                        className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                      >
                        <option value="">All Banner Types</option>

                        {bannerFilterOptions.bannerTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* SHOP CODE */}
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                        Shop Code
                      </label>

                      <select
                        value={bannerShopCodeFilter}
                        onChange={(e) =>
                          setBannerShopCodeFilter(e.target.value)
                        }
                        className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                      >
                        <option value="">All Shop Codes</option>

                        {bannerFilterOptions.shopCodes.map((shopCode) => (
                          <option key={shopCode} value={shopCode}>
                            {shopCode}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* ON TAP */}
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                        On Tap
                      </label>

                      <select
                        value={bannerOnTapFilter}
                        onChange={(e) => setBannerOnTapFilter(e.target.value)}
                        className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                      >
                        <option value="">All On Tap</option>

                        {bannerFilterOptions.onTapValues.map((onTap) => (
                          <option key={onTap} value={onTap}>
                            {onTap}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* RESET */}
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={handleResetBannerFilters}
                        disabled={!hasBannerFilters}
                        className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <RefreshCw size={15} />
                        Reset Filters
                      </button>
                    </div>
                  </div>
                </div>

                {bannerLoading ? (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {[1, 2, 3, 4].map((item) => (
                      <div
                        key={item}
                        className="aspect-video animate-pulse rounded-2xl bg-slate-100"
                      />
                    ))}
                  </div>
                ) : filteredBannerDetails.length > 0 ? (
                  <div
                    ref={bannerScrollRef}
                    className="no-scrollbar relative isolate z-0 flex items-stretch gap-4 overflow-x-auto overflow-y-hidden scroll-smooth pb-2"
                  >
                    {loopedBannerDetails.map((item, index) => (
                      <div
                        key={`${getBannerId(item)}-${index}`}
                        className="relative isolate z-0 flex w-full shrink-0 self-stretch sm:w-[calc(50%-8px)] lg:w-[calc(33.333333%-10.666667px)]"
                      >
                        <BannerCard
                          item={item}
                          onEdit={() => openEditBannerModal(item)}
                          onDelete={() => handleDeleteBanner(item)}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 py-10">
                    <EmptyState
                      icon={<ImageIcon size={26} />}
                      title={
                        hasBannerFilters
                          ? "No matching banners"
                          : bannerError
                            ? "Unable to load banners"
                            : "No banner found"
                      }
                      text={
                        hasBannerFilters
                          ? "No banners match the selected filters."
                          : bannerError ||
                            "Create your first promotional banner for the Chaafo UI."
                      }
                    />

                    <div className="mt-5 flex justify-center">
                      <button
                        onClick={openAddBannerModal}
                        className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-green-700"
                      >
                        <Plus size={17} />
                        Add Banner
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* CATEGORY DRAG & DROP ORDER SECTION */}
              <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-lg font-bold text-slate-900">
                        Category Display Order
                      </h2>

                      <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-600">
                        {displayedRestaurantCategories.length} Categories
                      </span>
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    {(adminId === "3" ||
                      adminId === "12" ||
                      adminId === "13") && (
                      <button
                        type="button"
                        onClick={handleUpdateAllCategorySortOrders}
                        disabled={
                          categorySortUpdatingId === "all" ||
                          displayedRestaurantCategories.length === 0
                        }
                        className="inline-flex h-10 items-center justify-center rounded-xl bg-emerald-600 px-4 text-sm font-bold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {categorySortUpdatingId === "all"
                          ? "Updating..."
                          : "Update Order"}
                      </button>
                    )}

                    <div className="flex overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                      <button
                        type="button"
                        onClick={() => handleCategorySliderScroll("left")}
                        disabled={displayedRestaurantCategories.length <= 10}
                        className="flex h-10 w-10 items-center justify-center border-r border-slate-200 text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-300"
                        aria-label="Previous categories"
                      >
                        <ChevronLeft size={20} />
                      </button>

                      <button
                        type="button"
                        onClick={() => handleCategorySliderScroll("right")}
                        disabled={displayedRestaurantCategories.length <= 10}
                        className="flex h-10 w-10 items-center justify-center text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-300"
                        aria-label="Next categories"
                      >
                        <ChevronRight size={20} />
                      </button>
                    </div>
                  </div>
                </div>

                {displayedRestaurantCategories.length > 0 ? (
                  <div className="relative min-w-0 overflow-hidden">
                    <div
                      ref={categorySliderRef}
                      onDragOver={handleCategorySliderDragOver}
                      className="no-scrollbar flex w-full snap-x snap-mandatory flex-nowrap items-stretch gap-3 overflow-x-auto overflow-y-hidden scroll-smooth pb-2"
                    >
                      {displayedRestaurantCategories.map((item, index) => {
                        const categoryId = String(getCategoryId(item));
                        const isActive =
                          String(selectedCategoryId) === String(categoryId);
                        const isDragging =
                          draggedCategoryId === String(categoryId);
                        const isDragOver =
                          dragOverCategoryId === String(categoryId) &&
                          draggedCategoryId !== String(categoryId);
                        const isAllCategory = isFixedAllCategory(item);
                        const isCategoryInactive =
                          getCategoryActiveStatus(item) === "N";

                        return (
                          <div
                            key={`category-circle-${categoryId || index}`}
                            draggable={
                              !isAllCategory &&
                              (adminId === "3" ||
                                adminId === "12" ||
                                adminId === "13")
                            }
                            onDragStart={(event) =>
                              handleCategoryDragStart(event, item)
                            }
                            onDragOver={(event) =>
                              handleCategoryDragOver(event, item)
                            }
                            onDrop={(event) => handleCategoryDrop(event, item)}
                            onDragEnd={handleCategoryDragEnd}
                            className={`relative w-[110px] min-w-[110px] shrink-0 snap-start rounded-2xl border p-2.5 transition-all duration-200 xl:w-[calc(10%_-_10.8px)] xl:min-w-[calc(10%_-_10.8px)] ${
                              isAllCategory
                                ? "cursor-default border-amber-200 bg-amber-50/60"
                                : isDragging
                                  ? "scale-95 cursor-grabbing border-emerald-300 bg-emerald-50 opacity-50"
                                  : isDragOver
                                    ? "border-emerald-500 bg-emerald-50 shadow-sm ring-2 ring-emerald-100"
                                    : "cursor-grab border-transparent bg-white hover:border-slate-200 hover:bg-slate-50"
                            }`}
                          >
                            {(adminId === "3" ||
                              adminId === "12" ||
                              adminId === "13") &&
                              (isAllCategory ? (
                                <div className="absolute right-1.5 top-1.5 rounded-lg bg-amber-100 px-2 py-1 text-[9px] font-black uppercase tracking-wide text-amber-700 ring-1 ring-amber-200">
                                  Fixed
                                </div>
                              ) : (
                                <div className="items-center justify-center"></div>
                              ))}

                            {isCategoryInactive && (
                              <div className="mb-1 flex justify-center">
                                <span className="rounded-full bg-red-100 px-2 py-0.5 text-[9px] font-bold text-red-600">
                                  Inactive
                                </span>
                              </div>
                            )}

                            <button
                              type="button"
                              className="group block w-full pt-2 text-center"
                              onClick={() => {
                                setSelectedCategoryId(categoryId);
                                setRestaurantSearch("");
                                setCategoryRestaurantSearch("");
                              }}
                            >
                              <div
                                className={`mx-auto aspect-square w-[76px] overflow-hidden rounded-full border-[3px] bg-slate-100 transition xl:w-[72%] ${
                                  isActive
                                    ? "border-emerald-500 shadow-md"
                                    : "border-white ring-1 ring-slate-200 group-hover:ring-emerald-300"
                                }`}
                              >
                                {getCategoryImage(item) ? (
                                  <img
                                    src={getCategoryImage(item)}
                                    alt={getCategoryName(item)}
                                    draggable={false}
                                    className="h-full w-full select-none object-cover"
                                    onError={(event) => {
                                      event.currentTarget.style.display =
                                        "none";
                                    }}
                                  />
                                ) : (
                                  <div className="flex h-full w-full items-center justify-center text-slate-400">
                                    <ImageIcon size={22} />
                                  </div>
                                )}
                              </div>

                              <p
                                className={`mt-2 min-h-[36px] break-words text-xs font-bold leading-[18px] ${
                                  isActive
                                    ? "text-emerald-700"
                                    : "text-slate-800"
                                }`}
                              >
                                {getCategoryName(item)}
                              </p>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 py-8 text-center text-sm text-slate-500">
                    No categories found
                  </div>
                )}
              </div>

              {/* CONTENT */}
              <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[320px_1fr]">
                {/* LEFT CATEGORY PANEL */}
                <div className="sticky top-0 flex max-h-[calc(100vh-180px)] min-h-0 flex-col rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="mb-4 flex shrink-0 items-center justify-between gap-3">
                    <div>
                      <h2 className="text-lg font-bold text-slate-900">
                        Categories
                      </h2>
                      <p className="text-sm text-slate-500">
                        {filteredCategories?.length || 0} categories found
                      </p>
                    </div>
                    {adminId === "3" || adminId === "12" || adminId === "13" ? (
                      <button
                        onClick={openAddCategoryModal}
                        className="rounded-lg bg-green-600 p-2.5 text-white transition"
                      >
                        <Plus size={18} />
                      </button>
                    ) : (
                      ""
                    )}
                  </div>

                  <div className="no-scrollbar min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain pr-1">
                    {filteredCategories?.length > 0 ? (
                      filteredCategories.map((item) => {
                        const categoryId = getCategoryId(item);
                        const isActive =
                          String(selectedCategoryId) === String(categoryId);

                        return (
                          <div
                            key={categoryId}
                            onClick={() => {
                              setSelectedCategoryId(String(categoryId));

                              // Global search se category mode me switch
                              setRestaurantSearch("");
                              setCategoryRestaurantSearch("");
                            }}
                            className={`group aspect-video cursor-pointer rounded-3xl border p-3 transition ${
                              isActive
                                ? "border-emerald-300 bg-emerald-50 shadow-sm"
                                : getCategoryActiveStatus(item) === "N"
                                  ? "border-red-200 bg-red-50/40 opacity-75 hover:opacity-100"
                                  : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                            }`}
                          >
                            <div className="flex h-full flex-col justify-between">
                              {/* TOP ROW: IMAGE 1:1 + CONTENT */}
                              <div className="flex min-h-0 gap-3">
                                {/* IMAGE 1:1 */}
                                <div className="h-20 w-20 shrink-0 overflow-hidden rounded-2xl bg-slate-100">
                                  {getCategoryImage(item) ? (
                                    <img
                                      src={getCategoryImage(item)}
                                      alt={getCategoryName(item)}
                                      className="h-full w-full object-cover"
                                      onError={(e) => {
                                        e.currentTarget.style.display = "none";
                                      }}
                                    />
                                  ) : (
                                    <div className="flex h-full w-full items-center justify-center text-slate-400">
                                      <ImageIcon size={22} />
                                    </div>
                                  )}
                                </div>

                                {/* CONTENT */}
                                <div className="min-w-0 flex-1">
                                  <div className="flex min-w-0 items-center gap-2">
                                    <h3 className="min-w-0 flex-1 truncate font-bold text-slate-900">
                                      {getCategoryName(item)}
                                    </h3>

                                    <span
                                      className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${
                                        getCategoryActiveStatus(item) === "Y"
                                          ? "bg-emerald-100 text-emerald-700"
                                          : "bg-red-100 text-red-600"
                                      }`}
                                    >
                                      {getCategoryActiveStatus(item) === "Y"
                                        ? "Active"
                                        : "Inactive"}
                                    </span>
                                  </div>

                                  <p className="mt-1 line-clamp-2 text-xs text-slate-500">
                                    {getCategoryDescription(item) ||
                                      "No description added"}
                                  </p>
                                </div>
                              </div>

                              {/* BOTTOM ROW */}
                              <div className="mt-2 flex w-full items-center justify-between gap-2">
                                <div className="min-w-0 rounded-full bg-emerald-50 px-2.5 py-1.5 text-[12px] font-bold text-emerald-700 ring-1 ring-emerald-100">
                                  <span className="whitespace-nowrap">
                                    {getCategoryRestaurantCount(item)}{" "}
                                    Restaurant
                                  </span>
                                </div>

                                {adminId === "3" ||
                                adminId === "12" ||
                                adminId === "13" ? (
                                  <div className="flex shrink-0 items-center gap-2">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openEditCategoryModal(item);
                                      }}
                                      className="rounded-xl bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 ring-1 ring-slate-200 hover:bg-slate-100"
                                    >
                                      Edit
                                    </button>

                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteCategory(item);
                                      }}
                                      className="rounded-xl bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-600 ring-1 ring-red-100 hover:bg-red-100"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                ) : (
                                  ""
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <EmptyState
                        icon={<Layers size={24} />}
                        title="No category found"
                        text="Add your first category to start mapping restaurants."
                      />
                    )}
                  </div>
                </div>

                {/* RIGHT RESTAURANT PANEL */}
                <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                  <div className="mb-5 flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                    <div>
                      <div className="mb-3 flex flex-wrap gap-1.5">
                        {[
                          { label: "Veg", value: "V" },
                          { label: "Non-Veg", value: "N" },
                          { label: "Both", value: "B" },
                        ].map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => {
                              setSelectedServingType(option.value);
                              setRestaurantSearch("");
                              setCategoryRestaurantSearch("");
                            }}
                            className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                              selectedServingType === option.value
                                ? "bg-emerald-600 text-white"
                                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                      <h2 className="mt-1 text-2xl font-bold text-slate-900">
                        {isGlobalRestaurantSearchActive
                          ? "All Restaurants"
                          : selectedCategory
                            ? getCategoryName(selectedCategory)
                            : "All Restaurants"}
                      </h2>

                      <p className="mt-1 text-sm text-slate-500">
                        {filteredRestaurants?.length || 0} restaurants found
                      </p>
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row">
                      <div className="flex min-w-[220px] items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                        <Search size={17} className="text-slate-400" />
                        <input
                          value={categoryRestaurantSearch}
                          onChange={(e) => {
                            const value = e.target.value;

                            setCategoryRestaurantSearch(value);

                            if (value.trim()) {
                              setRestaurantSearch("");
                            }
                          }}
                          disabled={!selectedCategoryId}
                          placeholder={
                            selectedCategoryId
                              ? "Search in this category..."
                              : "Select a category first"
                          }
                          className="w-full bg-transparent text-xs outline-none placeholder:text-slate-400"
                        />
                      </div>

                      {adminId === "3" ||
                      adminId === "12" ||
                      adminId === "13" ? (
                        <button
                          onClick={openAddRestaurantModal}
                          disabled={!selectedCategoryId}
                          className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-green-600 px-3 text-xs font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <Plus size={15} />
                          Add Restaurant
                        </button>
                      ) : (
                        ""
                      )}
                    </div>
                  </div>

                  {loading ? (
                    <RestaurantSkeleton />
                  ) : filteredRestaurants?.length > 0 ? (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                      {filteredRestaurants.map((item) => (
                        <RestaurantCard
                          key={getRestaurantId(item)}
                          item={item}
                          onEdit={() => openEditRestaurantModal(item)}
                          onDelete={() => handleDeleteRestaurant(item)}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 py-14">
                      <EmptyState
                        icon={<Store size={28} />}
                        title="No restaurants found"
                        text="Add restaurants inside this category to show them here."
                      />

                      {selectedCategoryId &&
                        (adminId == "3" ||
                          adminId == "12" ||
                          adminId == "13") && (
                          <div className="mt-5 flex justify-center">
                            <button
                              onClick={openAddRestaurantModal}
                              className="inline-flex items-center gap-2 rounded-2xl bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-600"
                            >
                              <Plus size={17} />
                              Add Restaurant
                            </button>
                          </div>
                        )}
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : activeWorkspaceTab === "highlights" ? (
            <CatalogHighlightsManager
              userId={selectedHighlightClientUserId}
              loading={masterCatalogLoading}
              catalogMenus={masterCatalogMenus}
              fetchCatalogueItemsMaster={fetchMasterCatalogMenus}
              updateCatalogRecommended={updateCatalogRecommended}
              updateCatalogOffers={updateCatalogOffers}
              updateCatalogSuggestion={updateCatalogSuggestion}
              clients={clients}
              selectedClientCode={selectedHighlightClientCode}
              onClientChange={handleHighlightClientChange}
            />
          ) : activeWorkspaceTab === "configuration" ? (
            <MessageConfigurationManager
              getMessageConfig={getMessageConfig}
              updateMessageConfig={updateMessageConfig}
              loginGateway={loginGateway}
              messageConfigLoading={messageConfigLoading}
              loginGatewayLoading={loginGatewayLoading}
              refreshKey={configurationRefreshKey}
            />
          ) : activeWorkspaceTab === "banner" ? (
            <Banner />
          ) : activeWorkspaceTab === "appBanner" ? (
            <AppBanner />
          ) : null}
        </div>
      </div>

      <ChaafoModals
        loading={loading}
        showCategoryModal={showCategoryModal}
        setShowCategoryModal={setShowCategoryModal}
        categoryMode={categoryMode}
        categoryForm={categoryForm}
        setCategoryForm={setCategoryForm}
        categoryImageUploading={categoryImageUploading}
        handleCategoryImageUpload={handleCategoryImageUpload}
        handleSaveCategory={handleSaveCategory}
        showBannerModal={showBannerModal}
        setShowBannerModal={setShowBannerModal}
        bannerForm={bannerForm}
        setBannerForm={setBannerForm}
        bannerImageUploading={bannerImageUploading}
        handleBannerImageUpload={handleBannerImageUpload}
        handleSaveBanner={handleSaveBanner}
        deleteModal={deleteModal}
        setDeleteModal={setDeleteModal}
        handleConfirmDelete={handleConfirmDelete}
        showRestaurantModal={showRestaurantModal}
        setShowRestaurantModal={setShowRestaurantModal}
        restaurantMode={restaurantMode}
        restaurantForm={restaurantForm}
        setRestaurantForm={setRestaurantForm}
        restaurantImageUploading={restaurantImageUploading}
        handleRestaurantImageUpload={handleRestaurantImageUpload}
        handleSaveRestaurant={handleSaveRestaurant}
        clients={clients}
      />
    </div>
  );
}